import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { cardHref } from '@/lists/cellValue'
import {
  ConfirmModal,
  Facts,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type Fact,
  type HeaderAction,
  type MenuAction,
} from './record'
import { CopyValue, labelOf, listPath } from './recordHelpers'
import type { RecordViewProps } from './types'
import {
  cancelReceipt,
  completeReceipt,
  fetchDiscrepancies,
  fetchReceipt,
  fetchReceiptTerminal,
  receiptLineMatches,
  startReceipt,
  type Discrepancy,
  type Putaway,
  type Receipt,
  type ReceiptLine,
} from './warehouseApi'
import {
  CellCode,
  HistoryTab,
  OperationAlerts,
  ProductCell,
  RowFilter,
  StatusBadge,
  cancelledEnd,
  copyLinkItem,
  documentState,
  momentOr,
  newOperationKey,
  qty,
  signedQty,
  stagesOf,
  sum,
  useOperation,
} from './warehouseRecord'
import '@/styles/record.css'

/**
 * Карточка приёмки на дизайн-системе (Receiving.dc.html, DarkReceiving.dc.html).
 *
 * Главное в приёмке — ожидаемое рядом с принятым и расхождения тремя отдельными видами:
 * недостача, излишек и брак — это три разные претензии поставщику, и одна колонка
 * «расхождение» прятала бы, о чём спорить. Разница приходит из акта сервера: домен решает,
 * входит ли брак в расхождение, и вторая формула здесь разошлась бы с ним молча.
 *
 * Ожидаемое показывается **всегда**, и это не то же, что слепой пересчёт: приёмку сверяют с
 * накладной поставщика, которая у кладовщика в руках.
 *
 * Действия — в шапке по статусу: черновик передают в работу, приёмку в работе проводят
 * (`/wms/receipts/{id}/complete`, то же право `update`, что у терминала), отмена — в «⋯» за
 * чертой и через подтверждение. Пересчёт строк в офис не выведен: он делается на ТСД.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - Коды маркировки по приёмке (счётчик, последние сканы, дубли): сервер не отдаёт их
 *   списком ни офису, ни терминалу.
 * - Ячейка строки, машина и водитель, ворота, суммы расхождений по закупочным ценам:
 *   в карточке приёмки этих полей нет.
 * - «Печать этикеток», «ТОРГ-2», «Изменить ворота», «Претензия поставщику», «Оприходовать
 *   излишек», «Акт о браке», «Заполнить из ЗП» и «Загрузить УПД»: таких операций API не
 *   публикует.
 * - Лента событий: единая лента (#79) складские документы не знает; вкладка «История»
 *   показывает журнал изменений записи.
 */
export function ReceiptRecordView({ recordId, record, form, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const [tab, setTab] = useState('lines')
  const [filter, setFilter] = useState<'all' | 'diff' | 'pending'>('all')
  const [confirm, setConfirm] = useState<'complete' | 'cancel' | null>(null)
  const [operationKey, setOperationKey] = useState('')
  const [putaway, setPutaway] = useState<Putaway[] | null>(null)
  const operation = useOperation([
    ['receipt', recordId],
    ['receipt-terminal', recordId],
    ['receipt-discrepancies', recordId],
    ['record', 'warehouse.receipt', recordId],
    ['list', 'warehouse.receipt'],
  ])

  const mayUpdate = can(user, 'warehouse.receipt', 'update')
  const mayCancel = can(user, 'warehouse.receipt', 'delete')

  const receiptQuery = useQuery({
    queryKey: ['receipt', recordId],
    queryFn: ({ signal }) => fetchReceipt(recordId, signal),
    retry: false,
  })
  const terminal = useQuery({
    queryKey: ['receipt-terminal', recordId],
    queryFn: ({ signal }) => fetchReceiptTerminal(recordId, signal),
    retry: false,
  })
  const act = useQuery({
    queryKey: ['receipt-discrepancies', recordId],
    queryFn: ({ signal }) => fetchDiscrepancies(recordId, signal),
    retry: false,
  })

  const start = useMutation({
    mutationFn: () => startReceipt(recordId),
    onSuccess: () => operation.succeed(t('card.wh.receipt.started')),
    onError: operation.fail,
  })
  const complete = useMutation({
    mutationFn: () => completeReceipt(recordId, operationKey),
    onSuccess: (result) => {
      setConfirm(null)
      setPutaway(result)
      operation.succeed(t('record.wh.rec.completed'))
    },
    onError: (error) => {
      setConfirm(null)
      operation.fail(error)
    },
  })
  const cancel = useMutation({
    mutationFn: () => cancelReceipt(recordId),
    onSuccess: () => {
      setConfirm(null)
      operation.succeed(t('card.wh.receipt.cancelled'))
    },
    onError: (error) => {
      setConfirm(null)
      operation.fail(error)
    },
  })

  const gate = documentState(receiptQuery, 'warehouse.receipt', '/receipts', t)
  if (gate) return gate
  const receipt = receiptQuery.data as Receipt

  const names = terminal.data?.catalogue?.names ?? {}
  // The "counted" flag is the server's; without the terminal view it is unknown, and an
  // unknown is not shown as "not yet accepted".
  const counted = new Map((terminal.data?.lines ?? []).map((line) => [line.id, line.counted]))
  const rows = act.data ?? []
  const byProduct = new Map(rows.map((row) => [row.productId, row]))
  const shortage = rows.filter((row) => row.discrepancy < 0)
  const surplus = rows.filter((row) => row.discrepancy > 0)
  const defect = rows.filter((row) => row.rejected > 0)
  const diffRows = new Set([...shortage, ...surplus, ...defect]).size
  const pendingCount = receipt.lines.filter((line) => counted.get(line.id) === false).length
  const countedCount = receipt.lines.filter((line) => counted.get(line.id) === true).length
  const open = receipt.status === 'draft' || receipt.status === 'in_progress'
  const withDiscrepancies = receipt.hasDiscrepancies || pendingCount > 0

  // ------------------------------------------------------------------ шапка

  const supplierName = labelOf(record.values, 'supplier_id')
  const supplierHref = receipt.supplierId ? cardHref(user, 'sales.counterparty', receipt.supplierId) : null
  const orderLabel = labelOf(record.values, 'purchase_order_id')
  const warehouseLabel = labelOf(record.values, 'warehouse_id')
  const keeperLabel = labelOf(record.values, 'received_by')
  const supplierDocument = record.values.supplier_document_number

  const meta = [
    <StatusBadge key="status" entity="warehouse.receipt" status={receipt.status} />,
    receipt.hasDiscrepancies ? (
      <span key="diff" className="flag flag-danger">
        <Icon name="alert" />
        {t('card.wh.receipt.hasDiscrepancies')}
      </span>
    ) : null,
    receipt.supplierId ? (
      <span key="supplier" className="row" style={{ gap: 6 }}>
        <Icon name="building" size="sm" />
        {supplierHref ? (
          <Link to={supplierHref}>{supplierName ?? t('record.wh.rec.supplier')}</Link>
        ) : (
          (supplierName ?? t('record.wh.rec.supplier'))
        )}
      </span>
    ) : (
      <span key="supplier" className="secondary">
        {t('record.wh.rec.noSupplier')}
      </span>
    ),
    orderLabel ? (
      <span key="order">
        {t('record.wh.rec.byOrder')} <span className="mono">{orderLabel}</span>
      </span>
    ) : null,
    warehouseLabel ? (
      <span key="warehouse" className="row" style={{ gap: 6 }}>
        <Icon name="box" size="sm" className="muted" />
        {t('record.wh.warehouseNamed', { name: warehouseLabel })}
      </span>
    ) : null,
    keeperLabel ? (
      <span key="keeper" className="row" style={{ gap: 6 }}>
        <Icon name="user" size="sm" className="muted" />
        {t('record.wh.rec.keeper', { name: keeperLabel })}
      </span>
    ) : null,
    <CopyValue key="copy" value={receipt.number} label={t('record.copyNumber', { number: receipt.number })} />,
  ]

  const openConfirm = (kind: 'complete' | 'cancel') => {
    operation.clear()
    setOperationKey(newOperationKey())
    setConfirm(kind)
  }

  // Nothing counted yet: the domain refuses to complete an empty receipt, and a button
  // that always fails teaches people to ignore failures.
  const nothingCounted = terminal.data !== undefined && countedCount === 0
  let primary: HeaderAction | null = null
  if (mayUpdate && receipt.status === 'draft') {
    primary = { id: 'start', label: t('record.wh.rec.start'), icon: 'arrow-right', onClick: () => start.mutate(), disabled: start.isPending }
  } else if (mayUpdate && receipt.status === 'in_progress') {
    primary = {
      id: 'complete',
      label: t('record.wh.rec.complete'),
      icon: 'check',
      onClick: () => openConfirm('complete'),
      disabled: nothingCounted || complete.isPending,
    }
  }

  const menu: MenuAction[] = [copyLinkItem(t, operation.setFailure)]
  if (mayCancel && open) {
    menu.push({ id: 'cancel', label: t('record.wh.rec.cancel'), icon: 'close', danger: true, onSelect: () => openConfirm('cancel') })
  }

  // ------------------------------------------------------------------ факты

  const facts: Fact[] = [
    {
      id: 'positions',
      label: t('record.wh.rec.positions'),
      value: qty(receipt.lines.length, locale),
      sub: terminal.data ? t('record.wh.rec.positionsSub', { counted: countedCount, pending: pendingCount }) : undefined,
    },
    {
      id: 'quantities',
      label: t('record.wh.rec.expectedAccepted'),
      value: `${qty(sum(receipt.lines, (line) => line.expected), locale)} / ${qty(sum(receipt.lines, (line) => line.accepted), locale)}`,
      sub: t('record.wh.rec.rejectedSub', { qty: qty(sum(receipt.lines, (line) => line.rejected), locale) }),
    },
    {
      id: 'discrepancies',
      label: t('card.tab.whDiscrepancies'),
      value: act.isError ? (
        '—'
      ) : diffRows > 0 ? (
        <span className="flag flag-danger" style={{ fontSize: 16, fontWeight: 600 }}>
          <Icon name="alert" />
          {t('record.wh.rec.diffCount', { count: diffRows })}
        </span>
      ) : (
        <span className="muted">{act.isLoading ? '…' : t('record.wh.none')}</span>
      ),
      sub:
        diffRows > 0
          ? t('record.wh.rec.diffSub', {
              shortage: qty(0 - sum(shortage, (row) => row.discrepancy) || 0, locale),
              surplus: qty(sum(surplus, (row) => row.discrepancy), locale),
              defect: qty(sum(defect, (row) => row.rejected), locale),
            })
          : undefined,
    },
    {
      id: 'supplier',
      label: t('record.wh.rec.supplier'),
      value: <span className="t-md" style={{ fontWeight: 500 }}>{supplierName ?? '—'}</span>,
      sub: typeof supplierDocument === 'string' && supplierDocument !== '' ? (
        <span className="mono">{t('record.wh.rec.supplierDoc', { number: supplierDocument })}</span>
      ) : undefined,
    },
    {
      id: 'received',
      label: t('record.wh.rec.receivedAt'),
      value: <span className="t-md" style={{ fontWeight: 500 }}>{momentOr(record.values.received_at, locale, t('record.wh.rec.notReceived'))}</span>,
    },
  ]

  // ------------------------------------------------------------------ вкладки

  const diffOf = (line: ReceiptLine) => byProduct.get(line.productId)
  const visible = receipt.lines.filter((line) => {
    if (filter === 'pending') return counted.get(line.id) === false
    if (filter === 'diff') {
      const row = diffOf(line)
      return line.rejected > 0 || (row !== undefined && row.discrepancy !== 0) || !receiptLineMatches(line)
    }
    return true
  })
  const diffLines = receipt.lines.filter(
    (line) => line.rejected > 0 || (diffOf(line)?.discrepancy ?? 0) !== 0 || !receiptLineMatches(line),
  ).length

  const linesPanel =
    receipt.lines.length === 0 ? (
      <StateView
        boxed
        kind="empty"
        title={t('record.wh.rec.empty.title')}
        description={t('card.wh.receipt.noLines')}
      />
    ) : (
      <Panel
        title={t('record.wh.rec.lines')}
        count={receipt.lines.length}
        flush
        actions={
          <RowFilter
            label={t('record.wh.rowFilter')}
            value={filter}
            onChange={setFilter}
            options={[
              { id: 'all', label: t('record.wh.filter.all') },
              { id: 'diff', label: t('record.wh.filter.withDiff'), count: diffLines },
              ...(terminal.data ? [{ id: 'pending' as const, label: t('record.wh.filter.pending'), count: pendingCount }] : []),
            ]}
          />
        }
      >
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl" aria-label={t('record.wh.rec.lines')}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>№</th>
                <th>{t('card.wh.line.product')}</th>
                <th className="num">{t('card.wh.receipt.expected')}</th>
                <th className="num">{t('card.wh.receipt.accepted')}</th>
                <th className="num">{t('card.wh.receipt.rejected')}</th>
                <th>{t('record.wh.rec.difference')}</th>
                <th>{t('record.wh.rec.progress')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((line) => (
                <ReceiptRow
                  key={line.id}
                  index={receipt.lines.indexOf(line) + 1}
                  line={line}
                  name={line.productName ?? names[line.productId]}
                  counted={counted.get(line.id)}
                  act={diffOf(line)}
                />
              ))}
              <tr className="total">
                <td />
                <td>{t('record.wh.rec.total', { count: receipt.lines.length })}</td>
                <td className="num">{qty(sum(receipt.lines, (line) => line.expected), locale)}</td>
                <td className="num">{qty(sum(receipt.lines, (line) => line.accepted), locale)}</td>
                <td className="num">{qty(sum(receipt.lines, (line) => line.rejected), locale)}</td>
                <td />
                <td className="t-sm">
                  {terminal.data ? t('record.wh.rec.countedOf', { counted: countedCount, total: receipt.lines.length }) : ''}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Counting is not brought to the office on purpose: a "accept 8 of 10" button here
            would let the warehouse be counted without anyone at the warehouse. */}
        <div className="panel-f secondary">{t('card.wh.receipt.countingOnTerminal')}</div>
      </Panel>
    )

  const sidebar = (
    <div className="rec-col">
      {putaway && <PutawayPanel rows={putaway} names={names} />}
      <Panel title={t('record.wh.rec.codes')}>
        <p className="secondary t-sm" style={{ margin: 0 }}>
          {t('record.wh.rec.codesMissing')}
        </p>
      </Panel>
      <Panel title={t('record.wh.details')}>{form}</Panel>
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[
          { label: t('nav.receipts'), to: listPath('warehouse.receipt', '/receipts') },
          { label: receipt.number, mono: true },
        ]}
        title={
          <>
            {t('record.wh.rec.title')} <span className="mono rec-mono-title">{receipt.number}</span>
          </>
        }
        meta={meta}
        primary={primary}
        menu={menu}
        menuLabel={t('record.more')}
      />

      <OperationAlerts failure={operation.failure} notice={operation.notice} />
      {notices}
      {primary?.id === 'complete' && nothingCounted && (
        <div className="alert alert-neutral" role="status">
          <Icon name="clock" />
          <span>{t('record.wh.rec.nothingCounted')}</span>
        </div>
      )}

      <StageBar
        ariaLabel={t('record.wh.stages')}
        steps={stagesOf(
          ['draft', 'in_progress', 'completed'],
          receipt.status === 'cancelled' ? -1 : ['draft', 'in_progress', 'completed'].indexOf(receipt.status),
          (code) => t(`record.wh.rec.stage.${code}`),
          (code) =>
            code === 'draft'
              ? momentOr(record.values.created_at, locale, '')
              : code === 'in_progress'
                ? receipt.status === 'in_progress'
                  ? t('record.wh.rec.stage.countingNow')
                  : ''
                : momentOr(record.values.received_at, locale, t('record.wh.rec.stage.afterPosting')),
        )}
        ends={cancelledEnd(receipt.status === 'cancelled', t('record.wh.cancelledEnd'))}
      />

      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'lines', label: t('card.tab.whReceiptLines'), count: receipt.lines.length },
          { id: 'diff', label: t('card.tab.whDiscrepancies'), count: act.data ? diffRows : null },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.wh.rec.sections')}
      >
        {tab === 'history' ? (
          <HistoryTab entity="warehouse.receipt" recordId={recordId} feedType="goods_receipt" />
        ) : (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">
              {tab === 'lines' ? (
                linesPanel
              ) : (
                <DiscrepancyPanels query={act} names={names} shortage={shortage} surplus={surplus} defect={defect} />
              )}
            </div>
            {sidebar}
          </div>
        )}
      </RecordTabs>

      <ConfirmModal
        open={confirm === 'complete'}
        icon={withDiscrepancies ? 'alert' : 'check'}
        title={withDiscrepancies ? t('record.wh.rec.confirm.withDiff') : t('record.wh.rec.confirm.title')}
        confirmLabel={withDiscrepancies ? t('record.wh.rec.confirm.withDiffAction') : t('record.wh.rec.complete')}
        pending={complete.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => complete.mutate()}
      >
        {diffRows > 0 && (
          <p>
            {t('record.wh.rec.confirm.diff', {
              shortage: qty(0 - sum(shortage, (row) => row.discrepancy) || 0, locale),
              surplus: qty(sum(surplus, (row) => row.discrepancy), locale),
              defect: qty(sum(defect, (row) => row.rejected), locale),
            })}
          </p>
        )}
        {pendingCount > 0 && <p>{t('record.wh.rec.confirm.pending', { count: pendingCount })}</p>}
        <p>{t('record.wh.rec.confirm.balance', { warehouse: warehouseLabel ?? '—' })}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.wh.rec.confirm.final')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={confirm === 'cancel'}
        icon="close"
        title={t('record.wh.rec.cancel.title', { number: receipt.number })}
        confirmLabel={t('record.wh.rec.cancel')}
        pending={cancel.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => cancel.mutate()}
      >
        <p>{t('record.wh.rec.cancel.text')}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

/**
 * Строка приёмки. «Разница» — из акта сервера; «ещё не принято» — по признаку пересчёта
 * с сервера, а не по нулю принятого: ноль означает и «ничего не доехало», и «не смотрели».
 */
function ReceiptRow({
  index,
  line,
  name,
  counted,
  act,
}: {
  index: number
  line: ReceiptLine
  name: string | undefined
  counted: boolean | undefined
  act: Discrepancy | undefined
}) {
  const { t, locale } = useApp()
  const done = line.accepted + line.rejected
  const percent = line.expected > 0 ? Math.min(100, Math.round((done / line.expected) * 100)) : done > 0 ? 100 : 0
  const pending = counted === false
  let difference: React.ReactNode
  if (pending) {
    difference = (
      <span className="badge b-info">
        <Icon name="clock" />
        {t('record.wh.rec.notAccepted')}
      </span>
    )
  } else if (act && act.discrepancy < 0) {
    difference = (
      <span className="flag flag-danger">
        <Icon name="alert" />
        {t('record.wh.rec.shortageBy', { qty: signedQty(act.discrepancy, locale) })}
      </span>
    )
  } else if (act && act.discrepancy > 0) {
    difference = (
      <span className="flag flag-warning">
        <Icon name="plus" />
        {t('record.wh.rec.surplusBy', { qty: signedQty(act.discrepancy, locale) })}
      </span>
    )
  } else if (!receiptLineMatches(line)) {
    difference = (
      <span className="flag flag-warning">
        <Icon name="alert" />
        {t('record.wh.rec.mismatch')}
      </span>
    )
  } else {
    difference = (
      <span className="flag flag-muted">
        <Icon name="check" />
        {t('record.wh.rec.matches')}
      </span>
    )
  }
  return (
    <tr className={pending ? 'is-selected' : undefined}>
      <td className="muted">{index}</td>
      <td className="wrap">
        <ProductCell id={line.productId} name={name} />
      </td>
      <td className="num">{qty(line.expected, locale)}</td>
      <td className="num strong">{qty(line.accepted, locale)}</td>
      <td className="num">
        {line.rejected > 0 ? (
          <span className="stack" style={{ gap: 0, alignItems: 'flex-end' }}>
            <span className="flag flag-warning">
              <Icon name="alert" />
              {qty(line.rejected, locale)}
            </span>
            {/* A defect without a reason cannot back a claim: the dash is work left to do. */}
            <span className="t-xs secondary">{line.rejectionReason ?? '—'}</span>
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>{difference}</td>
      <td>
        <div className="row" style={{ gap: 8 }}>
          <div
            className={pending ? 'meter' : 'meter is-success'}
            style={{ width: 80 }}
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${name ?? line.productId}: ${percent} %`}
          >
            <i style={{ width: `${percent}%` }} />
          </div>
          <span className="t-sm secondary">{percent} %</span>
        </div>
      </td>
    </tr>
  )
}

type ActQuery = { isLoading: boolean; isError: boolean; error: unknown; data: Discrepancy[] | undefined }

/**
 * Акт о расхождениях тремя видами. Проекция пересчёта, а не хранимый документ: копия
 * разошлась бы с ним при первой правке строки.
 */
function DiscrepancyPanels({
  query,
  names,
  shortage,
  surplus,
  defect,
}: {
  query: ActQuery
  names: Record<string, string>
  shortage: Discrepancy[]
  surplus: Discrepancy[]
  defect: Discrepancy[]
}) {
  const { t, locale } = useApp()
  if (query.isLoading) return <p className="muted">{t('app.loading')}</p>
  if (query.isError) {
    const forbidden = query.error instanceof ApiError && query.error.isForbidden
    return (
      <StateView
        boxed
        kind={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('access.denied') : t('record.wh.rec.actError')}
        description={query.error instanceof ApiError ? query.error.message : undefined}
      />
    )
  }
  if (shortage.length + surplus.length + defect.length === 0) {
    // "No discrepancies" is the outcome of the receipt, said in words: an empty table reads
    // as "not counted yet".
    return <StateView boxed kind="empty" title={t('card.wh.receipt.noDiscrepancies')} />
  }
  const group = (
    id: string,
    title: string,
    rows: Discrepancy[],
    flag: React.ReactNode,
    amount: (row: Discrepancy) => string,
  ) =>
    rows.length > 0 && (
      <Panel title={title} count={rows.length} flush actions={flag}>
        <table className="tbl" aria-label={title} data-group={id}>
          <thead>
            <tr>
              <th>{t('card.wh.line.product')}</th>
              <th className="num">{t('card.wh.receipt.expected')}</th>
              <th className="num">{t('card.wh.receipt.accepted')}</th>
              <th className="num">{t('record.wh.rec.amount')}</th>
              <th>{t('card.wh.receipt.reason')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.productId}>
                <td className="wrap">
                  <ProductCell id={row.productId} name={row.productName ?? names[row.productId]} sku={row.sku} />
                </td>
                <td className="num">{qty(row.expected, locale)}</td>
                <td className="num">{qty(row.accepted, locale)}</td>
                <td className="num strong">{amount(row)}</td>
                <td className="wrap">{row.reason ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    )
  return (
    <>
      {group(
        'shortage',
        t('card.wh.stocktake.shortage'),
        shortage,
        <span className="flag flag-danger">
          <Icon name="alert" />
          {qty(0 - sum(shortage, (row) => row.discrepancy) || 0, locale)}
        </span>,
        // The server's signed figure, not a client-side expected − accepted.
        (row) => signedQty(row.discrepancy, locale),
      )}
      {group(
        'surplus',
        t('card.wh.stocktake.surplus'),
        surplus,
        <span className="flag flag-warning">
          <Icon name="plus" />
          {qty(sum(surplus, (row) => row.discrepancy), locale)}
        </span>,
        (row) => signedQty(row.discrepancy, locale),
      )}
      {group(
        'defect',
        t('card.wh.receipt.rejected'),
        defect,
        <span className="flag flag-warning">
          <Icon name="alert" />
          {qty(sum(defect, (row) => row.rejected), locale)}
        </span>,
        (row) => qty(row.rejected, locale),
      )}
      <p className="secondary t-sm" style={{ margin: 0 }}>
        {t('record.wh.rec.moneyMissing')}
      </p>
    </>
  )
}

/** Куда класть принятое: ответ проведения. Ячейку подбирает сервер — пустую подходящую. */
function PutawayPanel({
  rows,
  names,
}: {
  rows: Putaway[]
  names: Record<string, string>
}) {
  const { t, locale } = useApp()
  return (
    <Panel title={t('record.wh.rec.putaway')} count={rows.length} flush>
      {rows.length === 0 ? (
        <div className="panel-b secondary">{t('record.wh.rec.putawayEmpty')}</div>
      ) : (
        <table className="tbl" aria-label={t('record.wh.rec.putaway')}>
          <thead>
            <tr>
              <th>{t('card.wh.line.product')}</th>
              <th className="num">{t('card.wh.line.quantity')}</th>
              <th>{t('card.wh.line.cell')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.itemId}>
                <td className="wrap">
                  <ProductCell id={row.productId} name={names[row.productId]} />
                </td>
                <td className="num">{qty(row.quantity, locale)}</td>
                <td>{row.crossDocked ? t('record.wh.rec.crossDock') : <CellCode id={row.cellId} code={row.cellCode} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}
